/**
 * Semantic Colors — Design tokens centralizados
 *
 * Fonte única de verdade para cores de acento usadas em dashboards,
 * cards de métricas, chips de status e indicadores visuais.
 *
 * Cores de marca de canal (WhatsApp, Instagram, etc.) NÃO estão aqui —
 * elas vivem em dashboardTheme.js (CHANNEL_COLORS) pois são externas.
 *
 * Baseado na paleta Tailwind-inspired definida em ui-refactoring-pattern.mdc.
 */

export const ACCENT = {
  success:    '#10b981',
  successBg:  'rgba(16, 185, 129, 0.12)',
  successDark:'#059669',
  successLight:'#34d399',

  danger:     '#ef4444',
  dangerBg:   'rgba(239, 68, 68, 0.12)',
  dangerDark: '#dc2626',
  dangerLight:'#f87171',

  warning:    '#f59e0b',
  warningBg:  'rgba(245, 158, 11, 0.12)',
  warningDark:'#d97706',
  warningLight:'#fbbf24',

  info:       '#3b82f6',
  infoBg:     'rgba(59, 130, 246, 0.12)',
  infoDark:   '#2563eb',
  infoLight:  '#90caf9',

  violet:     '#8b5cf6',
  violetBg:   'rgba(139, 92, 246, 0.12)',
  violetDark: '#7c3aed',
  violetLight:'#b794f6',

  neutral:    '#64748b',
  neutralBg:  'rgba(100, 116, 139, 0.12)',
  neutralDark:'#6b7280',
  neutralLight:'#9ca3af',

  cyan:       '#06b6d4',
  cyanBg:     'rgba(6, 182, 212, 0.12)',
  cyanDark:   '#0891b2',
  cyanLight:  '#22d3ee',

  indigo:     '#6366f1',
  indigoBg:   'rgba(99, 102, 241, 0.12)',
  indigoDark: '#4f46e5',
  indigoLight:'#818cf8',

  teal:       '#14b8a6',
  tealBg:     'rgba(20, 184, 166, 0.12)',
  tealDark:   '#0d9488',
  tealLight:  '#2dd4bf',

  pink:       '#ec4899',
  pinkBg:     'rgba(236, 72, 153, 0.12)',
  pinkDark:   '#db2777',
  pinkLight:  '#f472b6',

  brown:      '#a16207',
  brownBg:    'rgba(161, 98, 7, 0.12)',
  brownDark:  '#854d0e',
  brownLight: '#ca8a04',

  orange:     '#f97316',
  orangeBg:   'rgba(249, 115, 22, 0.12)',
  orangeDark: '#ea580c',
  orangeLight:'#fb923c',
};

/**
 * Mapa accentText: para cada "família" de accent, devolve o tom de TEXTO
 * adequado por modo. No light usamos o tom escurecido (legível sobre tint
 * claro); no dark usamos o tom claro (legível sobre superfície escura).
 *
 * Uso típico:
 *   const dark = theme.palette.type === 'dark';
 *   style={{ backgroundColor: ACCENT.successBg, color: accentText('success', dark) }}
 */
const ACCENT_TEXT = {
  success: { light: ACCENT.successDark, dark: ACCENT.successLight },
  danger:  { light: ACCENT.dangerDark,  dark: ACCENT.dangerLight },
  warning: { light: ACCENT.warningDark, dark: ACCENT.warningLight },
  info:    { light: ACCENT.infoDark,    dark: ACCENT.infoLight },
  violet:  { light: ACCENT.violetDark,  dark: ACCENT.violetLight },
  neutral: { light: ACCENT.neutralDark, dark: ACCENT.neutralLight },
  cyan:    { light: ACCENT.cyanDark,    dark: ACCENT.cyanLight },
  indigo:  { light: ACCENT.indigoDark,  dark: ACCENT.indigoLight },
  teal:    { light: ACCENT.tealDark,    dark: ACCENT.tealLight },
  pink:    { light: ACCENT.pinkDark,    dark: ACCENT.pinkLight },
  brown:   { light: ACCENT.brownDark,   dark: ACCENT.brownLight },
  orange:  { light: ACCENT.orangeDark,  dark: ACCENT.orangeLight },
};

export const accentText = (family, isDark) => {
  const pair = ACCENT_TEXT[family];
  if (!pair) return undefined;
  return isDark ? pair.dark : pair.light;
};

export const DECISION_COLORS = {
  respond:    ACCENT.success,
  escalate:   ACCENT.warning,
  transfer:   ACCENT.warning,
  turn_limit: ACCENT.orange,
  error:      ACCENT.danger,
  handoff:    ACCENT.violet,
  close:      ACCENT.neutral,
  fallback:   ACCENT.warning,
  skip:       ACCENT.neutral,
};

export const DECISION_STYLES = {
  respond:    { bg: ACCENT.successBg, color: ACCENT.successDark, label: 'Respondeu', family: 'success' },
  escalate:   { bg: ACCENT.warningBg, color: ACCENT.warningDark, label: 'Escalou para humano', family: 'warning' },
  transfer:   { bg: ACCENT.warningBg, color: ACCENT.warningDark, label: 'Transferiu para fila', family: 'warning' },
  turn_limit: { bg: ACCENT.orangeBg,  color: ACCENT.orangeDark,  label: 'Limite de turnos', family: 'orange' },
  error:      { bg: ACCENT.dangerBg,  color: ACCENT.dangerDark,  label: 'Erro técnico', family: 'danger' },
  handoff:    { bg: ACCENT.violetBg,  color: ACCENT.violetDark,  label: 'Outro agente IA', family: 'violet' },
  close:      { bg: ACCENT.neutralBg, color: ACCENT.neutralDark, label: 'Encerrou', family: 'neutral' },
  fallback:   { bg: ACCENT.warningBg, color: ACCENT.warningDark, label: 'Fallback', family: 'warning' },
  skip:       { bg: ACCENT.neutralBg, color: ACCENT.neutralDark, label: 'Ignorou', family: 'neutral' },
};
